import React, { useEffect } from "react";
import { useChatbotContext } from "../contexts/chatbotContext";
import { Button } from "primereact/button";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";

// Add DIRECTLINE_HOST to the Window interface
declare global {
  interface Window {
    DIRECTLINE_HOST: string;
    HOST_BEARER_TOKEN: string;
  }
}

const ChatWindow = styled.div<{ $fullScreen?: boolean }>`
  position: fixed;
  bottom: ${(props) => (props.$fullScreen ? "5vh" : "1.5rem")};
  right: ${(props) => (props.$fullScreen ? "5vw" : "1.5rem")};
  width: ${(props) => (props.$fullScreen ? "90vw" : "380px")};
  max-height: ${(props) => (props.$fullScreen ? "90vh" : "50vh")};
  height: ${(props) => (props.$fullScreen ? "90vh" : "auto")};
  z-index: 1050;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  border-radius: 1rem;
  padding: 0;
  overflow: auto;
  background: white;
  flex-direction: column;
  display: flex;
`;

const ChatHeader = styled.div`
  background: var(--sumi-blue);
  color: white;
  padding: 0.8rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  position: sticky;
  top: 0;
  z-index: 5000;

  & .p-button-label {
    color: #fff !important;
  }

  i {
    font-size: 1.2rem;
    color: #fff;
  }
`;

const ChatHeaderText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;

  i {
    font-size: 1.2rem;
    color: #fff;
  }
`;

const ChatHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChatBody = styled.div`
  min-height: 120px;
  background: #f9fafb;
  display: flex;
  overflow: auto;
  flex-direction: column-reverse;
`;

const ChatbotScreen: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen, setIsOpen, setScript, fullScreen, setFullScreen } =
    useChatbotContext();
  const [scriptLoaded, setScriptLoaded] = React.useState(false);
  const [bearerToken] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken])
  );

  useEffect(() => {
    if (!isOpen || scriptLoaded) return;

    window.DIRECTLINE_HOST = import.meta.env.VITE_DIRECTLINE_HOST;
    window.HOST_BEARER_TOKEN = bearerToken as string;
    const script = document.createElement("script");
    script.src = "https://sumi-online-chatweb.azurewebsites.net/embed.js";
    script.async = true;
    script.setAttribute("data-container", "webchat");
    script.setAttribute("data-locale", "fi-FI");

    script.onload = () => {
      setScriptLoaded(true);
    };

    setScript(script);
    const chatBody = document.getElementById("webchat");
    if (chatBody) {
      chatBody.appendChild(script);
    }
  }, [isOpen]);

  return (
    <ChatWindow
      style={{
        visibility: isOpen ? "visible" : "hidden",
        pointerEvents: isOpen ? "auto" : "none",
      }}
      $fullScreen={fullScreen}
    >
      <ChatHeader>
        <ChatHeaderText>
          <i className="pi pi-comments" />
          {t("header-sumion-ai-assistant")}
        </ChatHeaderText>
        <ChatHeaderActions>
          <Button
            icon={
              fullScreen ? "pi pi-window-minimize" : "pi pi-window-maximize"
            }
            text
            rounded
            aria-label="Toggle Fullscreen"
            onClick={() => setFullScreen && setFullScreen(!fullScreen)}
            style={{ color: "#fff" }}
          />
          <Button
            icon="pi pi-times"
            text
            rounded
            aria-label="Close"
            onClick={() => setIsOpen(false)}
            style={{ color: "#fff" }}
          />
        </ChatHeaderActions>
      </ChatHeader>

      <ChatBody id="webchat" role="main">
        <div className="loading">{t("loading-chat")}</div>
      </ChatBody>
    </ChatWindow>
  );
};

export default ChatbotScreen;
